import React, { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { fetchGatewayCatalog } from '../api';
import {
  GATEWAY_FIRST_LEVEL_ROUTES,
  assertFourGatewayRoutes,
} from '../gatewayRoutes';
import {
  consumeAuthorOpenedFromHub,
  consumeContentOpenedFromHub,
  consumeEditorialOpenedFromHub,
  markAuthorOpenedFromHub,
  markContentOpenedFromHub,
  markEditorialOpenedFromHub,
} from '../hubEntryFlags';
import type { CatalogResolved, GatewayRoute } from '../types';
import { AuthorWorkflowOverlay } from './AuthorWorkflowOverlay';
import { ContentPublisherOverlay } from './ContentPublisherOverlay';
import { EditorialProjectOverlay } from './EditorialProjectOverlay';
import { ResearchWizardOverlay } from './ResearchWizardOverlay';

assertFourGatewayRoutes();

interface RequestGatewayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  activeRole?: string;
  roleLabel?: string;
}

function releaseBodyLockIfIdle() {
  const openOverlay = document.querySelector('.rg-overlay');
  if (!openOverlay) {
    document.body.style.overflow = '';
  }
}

/**
 * U153 Create Request Hub — four independent first-level route cards.
 * Each card opens a real 1.83 module (Research / AU123 / R5.14 / Publisher).
 */
export const RequestGatewayModal: React.FC<RequestGatewayModalProps> = ({
  isOpen,
  onClose,
  onBack,
  activeRole = 'SYSTEM_OWNER',
  roleLabel,
}) => {
  const [catalog, setCatalog] = useState<CatalogResolved | null>(null);
  const [activeRoute, setActiveRoute] = useState<GatewayRoute | null>(null);
  const [visitedRoutes, setVisitedRoutes] = useState<Set<GatewayRoute>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const lastOpenRef = useRef<{ route: GatewayRoute | null; at: number }>({
    route: null,
    at: 0,
  });
  const activeRouteRef = useRef<GatewayRoute | null>(null);
  const routeHistoryPushedRef = useRef(false);

  useEffect(() => {
    activeRouteRef.current = activeRoute;
  }, [activeRoute]);

  const isCreateRequestEntryUrl = () => {
    const path = window.location.pathname;
    return path === '/app/requests/new' || path.startsWith('/app/requests/new/');
  };

  useEffect(() => {
    if (!isOpen) {
      // Temporary Close / exit: keep visited mounts so re-open restores in-memory context.
      // Role change (below) still resets; server drafts cover full remount / refresh.
      setActiveRoute(null);
      setCatalogError(null);
      routeHistoryPushedRef.current = false;
      releaseBodyLockIfIdle();
      return;
    }
    document.body.style.overflow = 'hidden';
    setLoading(true);
    setCatalogError(null);

    fetchGatewayCatalog(activeRole)
      .then((data) => {
        setCatalog(data);
      })
      .catch(() => {
        setCatalogError('Не удалось загрузить каталог маршрутов. Повторите попытку позже.');
        setCatalog({
          can_open: false,
          has_stage_zero: false,
          role_code: activeRole,
          directions_count: 0,
          services_count: 0,
          directions: [],
          services: [],
          client_categories: [],
          routes_allowed: [],
        });
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      releaseBodyLockIfIdle();
    };
  }, [isOpen, activeRole]);

  // Entry role/category change: drop route mounts so drafts reload under the new role.
  const prevRoleRef = useRef(activeRole);
  useEffect(() => {
    if (prevRoleRef.current === activeRole) return;
    prevRoleRef.current = activeRole;
    setVisitedRoutes(new Set());
    setActiveRoute(null);
  }, [activeRole]);

  /**
   * On `/app/requests/new`, browser Back while a route is open returns to Hub —
   * never leaves the entry or resurrects legacy `#/app/requests/new/*` layers.
   */
  useEffect(() => {
    if (!isOpen || !isCreateRequestEntryUrl()) return;

    const onPopState = () => {
      if (activeRouteRef.current != null) {
        setActiveRoute(null);
        consumeEditorialOpenedFromHub();
        consumeAuthorOpenedFromHub();
        consumeContentOpenedFromHub();
        routeHistoryPushedRef.current = false;
        const url = `${window.location.pathname}${window.location.search}`;
        window.history.pushState({ zertteuCreateHub: true }, '', url);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isOpen]);

  const returnToHub = useCallback(() => {
    setActiveRoute(null);
    // Drop route-entry flags when Hub is restored (button Back or browser Back trap).
    consumeEditorialOpenedFromHub();
    consumeAuthorOpenedFromHub();
    consumeContentOpenedFromHub();
    if (routeHistoryPushedRef.current && isCreateRequestEntryUrl()) {
      routeHistoryPushedRef.current = false;
      const url = `${window.location.pathname}${window.location.search}`;
      window.history.replaceState({ zertteuCreateHub: true }, '', url);
    } else {
      routeHistoryPushedRef.current = false;
    }
  }, []);

  const openRoute = useCallback(
    (routeId: GatewayRoute) => {
      // ROLE ACCESS: never open a route that catalog did not allow for this role.
      const allowed = catalog?.routes_allowed ?? [];
      if (catalog && (!catalog.can_open || !allowed.includes(routeId))) {
        return;
      }

      // One-click contract: ignore duplicate open only while that route is already active.
      const now = Date.now();
      if (
        activeRouteRef.current === routeId &&
        lastOpenRef.current.route === routeId &&
        now - lastOpenRef.current.at < 400
      ) {
        return;
      }
      lastOpenRef.current = { route: routeId, at: now };

      // Etalon U153 hub → module flags (paired back-handlers consume on Hub return).
      if (routeId === 'EDITORIAL') markEditorialOpenedFromHub();
      if (routeId === 'AUTHOR') markAuthorOpenedFromHub();
      if (routeId === 'CONTENT') markContentOpenedFromHub();

      setVisitedRoutes((prev) => {
        if (prev.has(routeId)) return prev;
        const next = new Set(prev);
        next.add(routeId);
        return next;
      });
      setActiveRoute(routeId);

      if (!isCreateRequestEntryUrl()) return;

      // Soft history entry so browser Back maps to Hub (not legacy create layers).
      if (!routeHistoryPushedRef.current) {
        const url = `${window.location.pathname}${window.location.search}`;
        window.history.pushState({ zertteuCreateRoute: routeId }, '', url);
        routeHistoryPushedRef.current = true;
      } else {
        window.history.replaceState({ zertteuCreateRoute: routeId }, '');
      }
    },
    [catalog]
  );

  if (!isOpen) return null;

  const handleBack = onBack ?? onClose;

  const displayRole =
    roleLabel ||
    (activeRole === 'SYSTEM_OWNER'
      ? 'Владелец системы'
      : activeRole === 'OPERATIONS_PARTNER'
        ? 'Операционный партнёр'
        : activeRole === 'RESEARCH_PARTNER' || activeRole === 'MENTOR_PARTNER'
          ? 'Научный партнёр'
          : activeRole === 'CLIENT'
            ? 'Клиент'
            : activeRole === 'AUTHOR'
              ? 'Автор / соавтор'
              : activeRole === 'PROJECT_EDITOR'
                ? 'Редактор проекта'
                : activeRole === 'LEARNER'
                  ? 'Докторант'
                  : activeRole === 'STUDENT' || activeRole === 'COURSE_STUDENT'
                    ? 'Обучающийся'
                    : activeRole);

  const isGuarded = catalog ? !catalog.can_open : false;
  const allowed = new Set(catalog?.routes_allowed ?? []);
  const showStageZero =
    Boolean(catalog?.has_stage_zero) ||
    activeRole === 'SYSTEM_OWNER' ||
    activeRole === 'OPERATIONS_PARTNER';

  const visibleRoutes = GATEWAY_FIRST_LEVEL_ROUTES.filter((route) => allowed.has(route.id));

  return (
    <>
      <div
        className={`rg-overlay rg-hub${activeRoute !== null ? ' rg-hidden' : ''}`}
        role="dialog"
        aria-modal={activeRoute === null}
        aria-hidden={activeRoute !== null}
        aria-label="Создать заявку"
      >
        <div className="rg-shell rg-hub-shell">
          <header className="rg-top rg-hub-top">
            <button type="button" className="rg-btn" onClick={handleBack}>
              ← Назад
            </button>
            <div className="rg-hub-title">
              <h2>Создать заявку</h2>
              <p>Единый вход в четыре самостоятельных маршрута</p>
            </div>
            <button type="button" className="rg-btn" onClick={onClose}>
              Закрыть ×
            </button>
          </header>

          <div className="rg-main rg-hub-main">
            {loading ? (
              <p style={{ color: '#8faebd' }}>Загрузка маршрутов…</p>
            ) : catalogError ? (
              <div className="rg-guard">
                <div className="rg-guard-ico">!</div>
                <h3>Каталог маршрутов недоступен</h3>
                <p>{catalogError}</p>
                <button type="button" className="rg-btn primary" onClick={onClose}>
                  Закрыть
                </button>
              </div>
            ) : isGuarded ? (
              <div className="rg-guard">
                <div className="rg-guard-ico">!</div>
                <h3>Создание общих заявок недоступно для вашей роли</h3>
                <p>
                  Для роли <strong>{displayRole}</strong> доступ к общему шлюзу ограничен. Используйте
                  инструменты вашего рабочего кабинета.
                </p>
                <button type="button" className="rg-btn primary" onClick={onClose}>
                  Вернуться в кабинет
                </button>
              </div>
            ) : (
              <>
                <div className="rg-intro">
                  <div>
                    <small>Создание и публикация</small>
                    <h3>Что вы хотите сделать?</h3>
                    <p>
                      Четыре маршрута разделены по назначению. Маршрут №04 открывает издательский
                      центр: публикации платформы, структурированные публичные каталоги и передача
                      материалов ответственному за соцсети / сайт ЕНУ.
                    </p>
                  </div>
                  <span className="rg-role-chip">{displayRole}</span>
                </div>

                <div className="rg-grid" data-rg-route-count={visibleRoutes.length}>
                  {visibleRoutes.map((route) => {
                    const bullets =
                      route.id === 'RESEARCH' && showStageZero
                        ? ['Этап 0: способ начала', ...route.bullets]
                        : route.bullets;
                    const canContinue = visitedRoutes.has(route.id);
                    const cta = canContinue ? 'Продолжить' : route.openCta;
                    return (
                      <button
                        key={route.id}
                        type="button"
                        className="rg-card"
                        style={{ '--rg': route.accent } as CSSProperties}
                        data-rg-route={route.etalonKey}
                        data-rg-module={route.moduleId}
                        aria-label={`${route.code} ${route.title}. ${cta}`}
                        onClick={() => openRoute(route.id)}
                      >
                        <div className="rg-card-head">
                          <span className="rg-ico" aria-hidden="true">
                            <svg viewBox="0 0 24 24">
                              <path d={route.iconPath} />
                            </svg>
                          </span>
                          <span className="rg-code">{route.code}</span>
                        </div>
                        <h4>{route.title}</h4>
                        <p>{route.text}</p>
                        <ul>
                          {bullets.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                        <span className="rg-open">
                          <span>{cta}</span>
                          <span aria-hidden="true">→</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Independent module mounts — keep visited routes alive so Back restores draft context. */}
      {visitedRoutes.has('RESEARCH') && (
        <ResearchWizardOverlay
          isOpen={activeRoute === 'RESEARCH'}
          onClose={onClose}
          onBack={returnToHub}
          catalog={catalog}
          activeRole={activeRole}
        />
      )}
      {visitedRoutes.has('AUTHOR') && (
        <AuthorWorkflowOverlay
          isOpen={activeRoute === 'AUTHOR'}
          onClose={onClose}
          onBack={returnToHub}
        />
      )}
      {visitedRoutes.has('EDITORIAL') && (
        <EditorialProjectOverlay
          isOpen={activeRoute === 'EDITORIAL'}
          onClose={onClose}
          onBack={returnToHub}
        />
      )}
      {visitedRoutes.has('CONTENT') && (
        <ContentPublisherOverlay
          isOpen={activeRoute === 'CONTENT'}
          onClose={onClose}
          onBack={returnToHub}
          activeRole={activeRole}
        />
      )}
    </>
  );
};
